import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, Title, Text, Grid, Button, Group, Divider, Alert, Stack, Anchor, Box, Loader } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import SquarePaymentForm from '../../components/Payment/SquarePaymentForm';
import { postApi } from '../../lib/api';
import squareLogo from '../../assets/square_logo.png';
import { REFUND_POLICY, REFUND_CONTACT } from '../../shared/refundPolicy';
import { usesCart } from '../../config/seasons';

/**
 * Step 4: Payment & Review
 * Shows the registration summary and the Square payment form. Prices come from
 * /api/price-quote; the server charges the same quote and marks the registration
 * paid, so nothing here decides what the dancer pays.
 */
const Step4Payment = ({
  registration,
  dancerInfo,
  variations,
  saving,
  goToPreviousStep,
  goToStep,
  refreshRegistration,
  readOnly = false
}) => {
  const navigate = useNavigate();
  const [paymentError, setPaymentError] = useState(null);
  const paymentErrorRef = useRef(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  // From /api/price-quote: why there's nothing to check out, and whether that's because everything is paid.
  const [cartProblem, setCartProblem] = useState(null);
  const [allPaid, setAllPaid] = useState(false);
  const [prices, setPrices] = useState(null);
  const [counts, setCounts] = useState(null);

  // 2027+: a paid dancer comes back here to pay for entries added later, so
  // only the flat-fee seasons (2026) bounce paid registrations to /success.
  const isCartSeason = usesCart(registration?.competition_year);
  const isAlreadyPaid = registration && !isCartSeason &&
    (registration.status === 'complete' || registration.payment_status === 'paid');

  useEffect(() => {
    if (isAlreadyPaid && !readOnly) {
      console.log('⚠️ Registration already paid, redirecting to success page');
      navigate('/register/individual/success');
    }
  }, [isAlreadyPaid, readOnly, navigate]);

  /**
   * Fetch a price quote. Returns the server's promoError (if any) so the promo
   * form can show it; the quote itself is always the one to display.
   */
  const fetchQuote = useCallback(async (promoCode) => {
    if (!registration?.id) return null;
    const data = await postApi('/api/price-quote', {
      registrationId: registration.id,
      promoCode: promoCode || undefined
    });
    setQuote(data.quote);
    setAppliedPromo(data.promo);
    setCartProblem(data.problem || null);
    setAllPaid(Boolean(data.allPaid));
    setPrices(data.prices || null);
    setCounts(data.counts || null);
    setQuoteError(null);
    return data.promoError;
  }, [registration?.id]);

  // Re-price when the registration or its variation count changes. Paid
  // registrations in view-only mode have nothing to quote.
  const variationCount = variations?.length || 0;
  useEffect(() => {
    if (readOnly) return;
    fetchQuote(appliedPromo?.code).catch((err) => {
      console.error('❌ Failed to load price quote:', err);
      setQuoteError(err.message);
    });
  }, [fetchQuote, variationCount, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyPromo = async (code) => {
    const promoError = await fetchQuote(code);
    if (promoError) throw new Error(promoError);
  };

  const handleRemovePromo = () => fetchQuote(null).catch((err) => setQuoteError(err.message));

  /**
   * Handle successful payment. The server has already recorded the payment,
   * marked the registration paid and sent the confirmation email; reload the
   * registration so the rest of the app sees it as paid.
   */
  const handlePaymentSuccess = async () => {
    // Registration fee already paid before this checkout = entries added later.
    const addedEntries = Boolean(registration?.reg_fee_payment_id);
    await refreshRegistration();
    navigate(addedEntries ? '/register/individual/success?added=true' : '/register/individual/success');
  };

  /**
   * Handle payment error
   */
  // The payment form reports every error here (null clears it), so there's one
  // error box. It sits above the form, so bring it into view.
  const handlePaymentError = (message) => setPaymentError(message);
  useEffect(() => {
    if (paymentError) paymentErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [paymentError]);

  return (
    <Grid gutter="xl">
      {/* Left Column - Payment Form (Wider) */}
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Paper
          radius="md"
          p={{ base: 'sm', sm: 'xl' }}
          withBorder
          style={{
            borderTop: '4px solid var(--primary-gold)',
            marginBottom: '1.5rem',
            paddingBottom: '12px'
          }}
          styles={{
            root: {
              '@media (max-width: 768px)': {
                padding: '16px !important'
              }
            }
          }}
        >
          <Title 
            order={3} 
            size="h4" 
            mb="lg" 
            style={{ 
              textAlign: 'left'
            }}
            styles={{
              root: {
                '@media (max-width: 768px)': {
                  fontSize: '1.25rem'
                }
              }
            }}
          >
            Secure Checkout
          </Title>

          {paymentError && (
            <Alert ref={paymentErrorRef} color="red" mb="xl" icon={<IconAlertCircle size="1rem" />}>
              <Text size="sm" style={{ textAlign: 'left' }}>{paymentError}</Text>
            </Alert>
          )}

          {/* Square Payment Form (hidden in view-only mode) */}
          {readOnly ? (
            <Alert color="green" variant="light" mb="md">
              <Text weight={600} mb="xs">Payment received</Text>
              <Text size="sm">
                Your payment for Prix du Canada {registration?.competition_year} has been recorded.
              </Text>
            </Alert>
          ) : (
            <>
              {quoteError && (
                <Alert color="red" mb="md" icon={<IconAlertCircle size="1rem" />}>
                  <Text size="sm" style={{ textAlign: 'left' }}>{quoteError}</Text>
                </Alert>
              )}
              {prices?.comboOpen && counts && counts.solos > counts.ensembles && counts.ensembles < 2 && (
                <Alert color="yellow" variant="light" mb="md">
                  <Text size="sm" mb="xs" style={{ textAlign: 'left' }}>
                    <strong>Solo + ensemble deal until {new Date(prices.comboEndsAt).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', timeZone: 'America/Toronto' })}:</strong>{' '}
                    a solo + an ensemble for ${prices.combo} — the discount is applied automatically at checkout.
                    Add an ensemble entry before you pay to save ${prices.comboSaving}.
                  </Text>
                  <Button size="xs" variant="outline" onClick={() => goToStep(3)}>Back to my entries</Button>
                </Alert>
              )}
              {!cartProblem && (
                <Text size="sm" mb="md" style={{ textAlign: 'left', color: 'var(--soft-gray)' }}>
                  Not ready with your music, piece or team? Pay now to hold your spot — you can finish the
                  details on My Entries any time until registration closes.
                </Text>
              )}
              {cartProblem ? (
                <Alert color={allPaid ? 'green' : 'yellow'} variant="light" mb="md">
                  <Text weight={600} mb="xs" style={{ textAlign: 'left' }}>
                    {allPaid ? "You're all paid up" : 'Nothing to check out yet'}
                  </Text>
                  <Text size="sm" mb="sm" style={{ textAlign: 'left' }}>
                    {allPaid
                      ? 'Everything on your registration is paid for. You can add another entry until registration closes.'
                      : cartProblem}
                  </Text>
                  <Group gap="sm">
                    <Button size="xs" variant="outline" onClick={() => goToStep(3)}>
                      {allPaid ? 'Add an entry' : 'Back to my entries'}
                    </Button>
                    {allPaid && (
                      <Button size="xs" variant="subtle" onClick={() => navigate('/profile')}>Go to my profile</Button>
                    )}
                  </Group>
                </Alert>
              ) : (
              <SquarePaymentForm
                quote={quote}
                appliedPromo={appliedPromo}
                registrationId={registration?.id}
                dancerInfo={dancerInfo}
                onApplyPromo={handleApplyPromo}
                onRemovePromo={handleRemovePromo}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
              )}
            </>
          )}

          {/* Combined Confirmation & Terms */}
          <Text 
            size="xs" 
            color="dimmed" 
            align="center" 
            mt="md" 
            style={{ 
              textAlign: 'center', 
              lineHeight: 1.6,
              fontSize: '12px'
            }}
          >
            You will receive a confirmation email after payment. By completing your payment, you agree to our{' '}
            <Anchor 
              size="xs" 
              href="/terms" 
              target="_blank"
              style={{ fontSize: '12px' }}
            >
              Terms of Service
            </Anchor>
            {' '}and{' '}
            <Anchor 
              size="xs" 
              href="/privacy" 
              target="_blank"
              style={{ fontSize: '12px' }}
            >
              Privacy Policy
            </Anchor>.
          </Text>

          {/* Divider */}
          <Divider my="lg" />

          {/* Powered by Square */}
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '8px 0 1px 0',
            }}
          >
            <Text
              size="sm"
              weight={500}
              color="dimmed"
              style={{
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: 'clamp(11px, 3vw, 13px)',
                lineHeight: '24px',
                marginTop: '2px',
              }}
            >
              Powered by
            </Text>
            <img
              src={squareLogo}
              alt="Square"
              style={{
                height: 'clamp(20px, 5vw, 24px)',
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
                marginTop: '2px',
              }}
            />
          </Box>
        </Paper>

        {/* Refund Policy (the pay button's checkbox links here) */}
        <Paper 
          id="refund-policy"
          p="md" 
          withBorder
          styles={{
            root: {
              '@media (max-width: 768px)': {
                padding: '12px !important'
              }
            }
          }}
        >
          <Text size="md" weight={600} mb="xs" style={{ textAlign: 'left' }}>Refund Policy</Text>
          <Stack gap={4}>
            {REFUND_POLICY.map(({ item, policy }) => (
              <Text key={item} size="sm" color="dimmed" style={{ textAlign: 'left', fontSize: '14px', lineHeight: '1.5' }}>
                <strong style={{ color: 'var(--charcoal)' }}>{item}:</strong> {policy}
              </Text>
            ))}
            <Text size="sm" color="dimmed" mt={4} style={{ textAlign: 'left', fontSize: '14px', lineHeight: '1.5' }}>
              {REFUND_CONTACT.replace('info@prixducanada.com', '')}
              <a href="mailto:info@prixducanada.com" style={{ color: 'var(--primary-gold)' }}>info@prixducanada.com</a>
            </Text>
          </Stack>
        </Paper>
      </Grid.Col>

      {/* Right Column - Registration Summary (Narrower) */}
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Paper
          radius="md"
          p={{ base: 'sm', sm: 'xl' }}
          withBorder
          style={{
            borderTop: '4px solid var(--primary-gold)',
            marginBottom: '1.5rem'
          }}
          styles={{
            root: {
              '@media (min-width: 769px)': {
                position: 'sticky',
                top: '20px'
              },
              '@media (max-width: 768px)': {
                padding: '16px !important'
              }
            }
          }}
        >
          <Title order={2} size="h4" mb="md" style={{ color: 'var(--charcoal)', textAlign: 'left' }}>
            Order Summary
          </Title>

          <Divider mb="xl" />

          {/* Personal Information Summary */}
          <Stack spacing="sm" mb="md">
            <div>
              <Text size="xs" weight={600} color="dimmed" mb="xs" style={{ textAlign: 'left' }}>DANCER</Text>
              <Text size="sm" weight={600} style={{ textAlign: 'left' }}>
                {dancerInfo?.first_name} {dancerInfo?.last_name}
              </Text>
              <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
                {dancerInfo?.email}
              </Text>
            </div>
          </Stack>

          <Divider mb="md" />

          {/* Variations Summary */}
          <div mb="md">
            <Group justify="space-between" mb="xs">
              <Text size="xs" weight={600} color="dimmed" style={{ textAlign: 'left' }}>VARIATIONS</Text>
              <Anchor size="xs" onClick={() => goToStep(3)}>Edit</Anchor>
            </Group>

            {variations && variations.length > 0 ? (
              <Stack spacing="xs">
                {variations.map((variation, index) => (
                  <div key={index}>
                    <Text size="sm" weight={600} style={{ textAlign: 'left' }}>{variation.variation_name}</Text>
                    <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
                      {variation.ballet_origin}
                    </Text>
                  </div>
                ))}
              </Stack>
            ) : (
              <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
                No variations selected yet
              </Text>
            )}
          </div>

          <Divider my="md" />

          {/* Pricing Breakdown (from /api/price-quote; not shown once paid) */}
          {!readOnly && (
          <Stack spacing="xs">
            <Text size="xs" weight={600} color="dimmed" mb="xs" style={{ textAlign: 'left' }}>PRICING</Text>

            {!quote ? (
              <Group justify="center" py="sm"><Loader size="sm" /></Group>
            ) : (
            <>
            {quote.lines.map((line, i) => (
              <Group key={`${line.type}-${i}`} justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ textAlign: 'left' }}>
                  <Text size="sm">
                    {line.quantity > 1 ? `${line.label} × ${line.quantity}` : line.label}
                  </Text>
                  {line.detail && <Text size="xs" color="dimmed">{line.detail}</Text>}
                </div>
                {/* Discounts are a plain "-$20.00", not a colour: colour alone
                    read as an added charge in testing. */}
                <Text size="sm" weight={600} style={{ whiteSpace: 'nowrap' }}>
                  {line.regularAmount && (
                    <span style={{ textDecoration: 'line-through', marginRight: '8px', fontWeight: 400, color: '#868e96' }}>
                      ${line.regularAmount.toFixed(2)}
                    </span>
                  )}
                  {line.amount < 0 ? '-' : ''}${Math.abs(line.amount).toFixed(2)}
                </Text>
              </Group>
            ))}

            {quote.earlyBirdSavings > 0 && (
              <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
                You're saving ${quote.earlyBirdSavings.toFixed(2)} in total (already taken off above).
              </Text>
            )}

            {/* Professional Video Package (Free Gift) */}
            <Group justify="space-between">
              <Text size="sm" style={{ textAlign: 'left' }}>
                Professional Video Package
              </Text>
              <Text size="sm" weight={600}>
                <span style={{ textDecoration: 'line-through', marginRight: '8px' }}>$100.00</span>
                $0
              </Text>
            </Group>

            {quote.discount > 0 && (
              <>
                <Group justify="space-between">
                  <Text size="sm" style={{ textAlign: 'left' }}>Subtotal</Text>
                  <Text size="sm" weight={600}>${quote.subtotal.toFixed(2)}</Text>
                </Group>

                <Group justify="space-between">
                  <Text size="sm" style={{ textAlign: 'left' }}>
                    {appliedPromo?.code ? `Promo discount (${appliedPromo.code})` : 'Promo discount'}
                  </Text>
                  <Text size="sm" weight={600} style={{ whiteSpace: 'nowrap' }}>
                    -${quote.discount.toFixed(2)}
                  </Text>
                </Group>
              </>
            )}

            {/* Subtotal before tax */}
            <Group justify="space-between">
              <Text size="sm" style={{ textAlign: 'left' }}>
                {quote.discount > 0 ? 'Subtotal after discount' : 'Subtotal'}
              </Text>
              <Text size="sm" weight={600}>${quote.subtotalAfterDiscount.toFixed(2)}</Text>
            </Group>

            {/* HST */}
            <Group justify="space-between">
              <Text size="sm" weight={500} style={{ textAlign: 'left', color: '#495057' }}>
                + HST ({Math.round(quote.hstRate * 100)}% Ontario)
              </Text>
              <Text size="sm" weight={600} style={{ color: '#495057' }}>${quote.hst.toFixed(2)}</Text>
            </Group>

            <Divider my="xs" />

            <Group justify="space-between">
              <Text size="md" weight={700} style={{ textAlign: 'left' }}>Total</Text>
              <div style={{ textAlign: 'right' }}>
                <Text size="lg" weight={700} style={{ color: 'var(--primary-gold)', display: 'inline' }}>
                  ${quote.total.toFixed(2)}
                </Text>
                <Text size="xs" color="dimmed" style={{ display: 'inline', marginLeft: '4px' }}>
                  CAD
                </Text>
              </div>
            </Group>
            </>
            )}
          </Stack>
          )}
        </Paper>
      </Grid.Col>

      {/* Navigation Buttons */}
      <Grid.Col span={12}>
        <Group
          justify="space-between"
          mt="xl"
          style={{
            padding: '0 8px'
          }}
        >
          <Button
            variant="subtle"
            onClick={goToPreviousStep}
            disabled={saving}
            style={{
              minHeight: '44px'
            }}
          >
            Back
          </Button>
          {readOnly && (
            <Button
              onClick={() => navigate('/profile')}
              style={{
                background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                minHeight: '44px'
              }}
            >
              Return to My Profile
            </Button>
          )}
        </Group>
      </Grid.Col>
    </Grid>
  );
};

export default Step4Payment;
